import { Input } from '@ordo/ui/input'
import type { ReactElement } from 'react'

import type { FieldTripMealOption } from '@/lib/school-orders/field-trip'

/** The column fields the inputs read (a subset of `OrderColumn`). */
interface QuantityColumn {
  columnId: string
  label: string
  studentCount: number | null
}

function QuantityInput({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}): ReactElement {
  return (
    <Input
      type="number"
      inputMode="numeric"
      min={0}
      placeholder="0"
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-20 shrink-0 text-sm"
    />
  )
}

function TotalRow({ heading, total }: { heading: string | null; total: number }): ReactElement {
  return (
    <div className="flex items-center justify-between border-t pt-2.5">
      <span className="text-sm font-medium text-foreground">{heading ? `${heading} total` : 'Total per day'}</span>
      <span className="text-sm font-semibold tabular-nums text-foreground">
        {total} {total === 1 ? 'meal' : 'meals'}
      </span>
    </div>
  )
}

function Heading({ heading }: { heading: string | null }): ReactElement | null {
  return heading ? <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{heading}</p> : null
}

function ColumnLabel({ col }: { col: QuantityColumn }): ReactElement {
  return (
    <span className="min-w-0 flex-1 truncate text-sm text-foreground">
      {col.label}
      {col.studentCount !== null ? <span className="text-muted-foreground"> · {col.studentCount} students</span> : null}
    </span>
  )
}

/** One quantity per column — the grid a mealtime with a single meal uses. */
export function GroupQuantityGrid({
  heading,
  columns,
  quantityFor,
  onQuantityChange,
  total,
}: {
  heading: string | null
  columns: readonly QuantityColumn[]
  quantityFor: (columnId: string) => string
  onQuantityChange: (columnId: string, value: string) => void
  total: number
}): ReactElement {
  return (
    <div className="flex flex-col gap-2">
      <Heading heading={heading} />
      <div className="flex max-h-56 flex-col gap-2 overflow-y-auto">
        {columns.map((col) => (
          <div key={col.columnId} className="flex items-center justify-between gap-3">
            <ColumnLabel col={col} />
            <QuantityInput
              label={`${col.label} meals per day`}
              value={quantityFor(col.columnId)}
              onChange={(value) => onQuantityChange(col.columnId, value)}
            />
          </div>
        ))}
      </div>
      <TotalRow heading={heading} total={total} />
    </div>
  )
}

/**
 * Per-meal quantity inputs for one mealtime that offers more than one
 * field-trip meal. A school ordering as one group gets one input per meal;
 * otherwise each meal lists the school's columns. The total is the sum of
 * every meal.
 */
export function FieldTripMealQuantities({
  heading,
  options,
  columns,
  quantityFor,
  onQuantityChange,
  total,
}: {
  /** The mealtime label, shown when the request covers more than one mealtime. */
  heading: string | null
  options: readonly FieldTripMealOption[]
  columns: readonly QuantityColumn[]
  quantityFor: (menuItemId: string, columnId: string) => string
  onQuantityChange: (menuItemId: string, columnId: string, value: string) => void
  total: number
}): ReactElement {
  const singleColumn = columns.length === 1 ? columns[0]! : null
  return (
    <div className="flex flex-col gap-2" data-testid="field-trip-meal-quantities">
      <Heading heading={heading} />
      <div className="flex max-h-72 flex-col gap-3 overflow-y-auto">
        {options.map((option) =>
          singleColumn ? (
            <div key={option.menuItemId} className="flex items-center justify-between gap-3">
              <span className="min-w-0 flex-1 text-sm text-foreground">{option.title}</span>
              <QuantityInput
                label={`${option.title} meals`}
                value={quantityFor(option.menuItemId, singleColumn.columnId)}
                onChange={(value) => onQuantityChange(option.menuItemId, singleColumn.columnId, value)}
              />
            </div>
          ) : (
            <div key={option.menuItemId} className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-foreground">{option.title}</span>
              {columns.map((col) => (
                <div key={col.columnId} className="flex items-center justify-between gap-3 pl-3">
                  <ColumnLabel col={col} />
                  <QuantityInput
                    label={`${option.title}, ${col.label} meals`}
                    value={quantityFor(option.menuItemId, col.columnId)}
                    onChange={(value) => onQuantityChange(option.menuItemId, col.columnId, value)}
                  />
                </div>
              ))}
            </div>
          ),
        )}
      </div>
      <TotalRow heading={heading} total={total} />
    </div>
  )
}
